'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { authApi, UserProfile } from '@/lib/api/auth';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authApi
      .me()
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const data = await authApi.register({
      email,
      password,
      full_name: metadata?.fullName || '',
      role: metadata?.role || 'learner',
      language_pref: metadata?.languagePref || 'en',
      phone: metadata?.phone || '',
      age: metadata?.age ?? null,
      gender: metadata?.gender || '',
      city: metadata?.city || '',
    });
    setProfile(data);
    return data;
  };

  const signIn = async (identifier: string, password: string, role?: string) => {
    const data = await authApi.login(identifier, password, role);
    setProfile(data);
    return { user: data, profile: data };
  };

  const signOut = async () => {
    await authApi.logout();
    setProfile(null);
  };

  const getCurrentUser = async () => profile;

  const isEmailVerified = () => true;

  const getUserProfile = async () => {
    const data = await authApi.me();
    setProfile(data);
    return data;
  };

  const value = {
    user: profile,
    profile,
    session: profile ? { user: profile } : null,
    loading,
    userRole: profile?.role || 'learner',
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:79:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="79" data-component-end-line="79" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>;
};
